import React, { useEffect } from "react";
import type { GetServerDataProps } from "gatsby";
import { getYear, parseISO, formatISO } from "date-fns";

import { MetaComponent } from "@bared/ui";
import { ArticleWithNextPrevious, filterProjection } from "@bared/groq";

import type { HeadProps, PageProps } from "~/types/gatsby";

import { useTripleWhale } from "~/hooks/useTripleWhale";

import { contextFromServerDataProps } from "~/lib/server";
import { seoConfigForPage, getSeoMeta, type SeoSchema } from "~/lib/seo";

import { ErrorPage } from "~/components/Error/Error";
import { Article } from "~/components/Article/Article";

const pageQuery = filterProjection(
  ArticleWithNextPrevious,
  `_type == "article" && slug.current == $slug && language == $language`,
);

export async function getServerData(props: GetServerDataProps) {
  // Resolve context.
  const context = await contextFromServerDataProps(props);

  // Expand context.
  const {
    respond,
    notFound,
    groq,
    locale,
    urlContext,
    sectionResolver,
    sanityImageUrlBuilder,
  } = context;

  // Resolve slug.
  const slug = props.params?.["slug"];
  if ("string" !== typeof slug) throw new Error("No slug resolved.");

  // Fetch content from Sanity.
  const [pageDefault, pageLocalised] = await Promise.all([
    groq(pageQuery, {
      slug,
      locale: null,
      language: "default",
    }),
    groq(pageQuery, {
      slug,
      locale: locale.identifier,
      language: locale.identifier,
    }),
  ]);

  // Pick a page.
  const page = pageLocalised ?? pageDefault;

  // Page not found in Sanity, return 404.
  if (!page) return notFound();

  // Compute dates.
  const updatedDate = page._updatedAt ? parseISO(page._updatedAt) : new Date();
  const publishedDate = page.attributes.publishedAt
    ? parseISO(page.attributes.publishedAt)
    : new Date();

  // Compute image.
  const image = page.image
    ? sanityImageUrlBuilder.image(page.image).width(1200).url()
    : null;

  // Create JSON-LD schema for article.
  const articleSchema: SeoSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    copyrightYear: getYear(publishedDate),
    articleBody: page.summary ?? undefined,
    author: { "@type": "Person", name: "Bared Footwear" },
    copyrightHolder: { "@type": "Person", name: "Bared Footwear" },
    creator: { "@type": "Person", name: "Bared Footwear" },
    dateModified: formatISO(updatedDate),
    datePublished: formatISO(publishedDate),
    description: page.summary ?? undefined,
    headline: page.title ?? undefined,
    image: image
      ? {
          "@type": "ImageObject",
          url: image,
          description: page.title ?? undefined,
        }
      : undefined,
    inLanguage: locale.languageCode,
    name: page.title ?? undefined,
    url: urlContext.canonicalUrl,
  };

  // Resolve seo config.
  const seo = seoConfigForPage({
    context,
    sanityMetadata: page.metadata,
    schemas: [articleSchema],
  });

  // Format the "relatedArticles" prop in the old way.
  // @todo Remove this when the Article component is able to just use the "nextArticle" and "prevArticle" atts directly from the page.
  const relatedArticles = {
    edges: [
      {
        node: { slug: page.slug, title: page.title },
        next: page.nextArticle && {
          slug: page.nextArticle.slug,
          title: page.nextArticle.title,
        },
        previous: page.prevArticle && {
          slug: page.prevArticle.slug,
          title: page.prevArticle.title,
        },
      },
    ],
  };

  // Resolve sections.
  page.content = await sectionResolver.resolve({
    sanitySections: page.content,
  });

  // Respond with server data.
  return respond({ page, relatedArticles, seo } as const);
}

export type ServerData = Awaited<ReturnType<typeof getServerData>>["props"];

export default function ArticlePage({ serverData }: PageProps<ServerData>) {
  const tripleWhale = useTripleWhale();

  // Send tracking events when server data changes.
  useEffect(() => {
    if (200 !== serverData.status) return;

    const { page } = serverData;

    tripleWhale.trackPageLoad("Article");
  }, [tripleWhale, serverData]);

  // Page found, render page.
  if (200 === serverData.status) {
    return (
      <Article
        article={serverData.page}
        relatedArticles={serverData.relatedArticles}
      />
    );
  }

  // Page not found, render error page.
  if (404 === serverData.status) {
    return <ErrorPage page={serverData.errorPage} />;
  }

  return null;
}

export function Head({ serverData }: HeadProps<ServerData>) {
  if (!serverData) return null;
  return (
    <MetaComponent
      meta={getSeoMeta(serverData.context.rootData.seo, serverData.seo)}
    />
  );
}
